import {AbstractControl} from '@angular/forms';
import {KEYS} from '../../translation-keys';

/** Naming rules of a variable scope, matching the pattern validators used in the editor. */
export interface VariableNameRules {
  min: number;
  max: number;
  digitStart: boolean;
}

export const VARIABLE_NAME_RULES: VariableNameRules = {min: 3, max: 50, digitStart: false};
export const GDT_VARIABLE_NAME_RULES: VariableNameRules = {min: 2, max: 49, digitStart: true};

/** The concrete problem of an invalid name, so the editor can explain it in plain words. */
export interface VariableNameProblem {
  type: 'invalidChars' | 'digitStart' | 'tooShort' | 'tooLong';
  /** number of missing or surplus characters */
  count?: number;
  /** offending characters, each listed once */
  chars?: string[];
}

/** Returns the first problem of the given name or null, when the name follows the rules. */
export function describeVariableNameProblem(name: string, rules: VariableNameRules = VARIABLE_NAME_RULES): VariableNameProblem {
  const value = name || '';
  const invalid = Array.from(new Set(value.match(/[^a-zA-Z0-9_]/g) || []));
  if (invalid.length) {
    return {type: 'invalidChars', chars: invalid};
  }
  if (!rules.digitStart && /^[0-9]/.test(value)) {
    return {type: 'digitStart'};
  }
  if (value.length < rules.min) {
    return {type: 'tooShort', count: rules.min - value.length};
  }
  if (value.length > rules.max) {
    return {type: 'tooLong', count: value.length - rules.max};
  }
  return null;
}

/**
 * Explains in plain words why a changed variable name is not accepted, empty when it is.
 * Shared by the question variables and the formula, scoring and extern variable settings
 */
export function variableNameMessage(control: AbstractControl, translate: (key: string) => string,
  rules: VariableNameRules = VARIABLE_NAME_RULES): string {
  if (!control || !control.dirty || !control.errors) {
    return '';
  }
  const errors = control.errors;
  if (errors.duplicateVariableName) {
    return translate(KEYS.EDITOR.DUPLICATE_VAR_NAME_TEXT);
  }
  if (errors.required) {
    return translate(KEYS.EDITOR.VAR_NAME_TOO_SHORT).replace('%s', String(rules.min));
  }
  if (!errors.pattern) {
    return '';
  }
  const problem = describeVariableNameProblem(control.value, rules);
  switch (problem && problem.type) {
    case 'invalidChars':
      return translate(KEYS.EDITOR.VAR_NAME_INVALID_CHARS).replace('%s', problem.chars
        .map(char => char === ' ' ? translate(KEYS.EDITOR.VAR_NAME_SPACE) : `„${char}“`).join(', '));
    case 'digitStart':
      return translate(KEYS.EDITOR.VAR_NAME_DIGIT_START);
    case 'tooShort':
      return translate(KEYS.EDITOR.VAR_NAME_TOO_SHORT).replace('%s', String(rules.min));
    case 'tooLong':
      return translate(KEYS.EDITOR.VAR_NAME_TOO_LONG).replace('%s', String(rules.max));
    default:
      return translate(KEYS.EDITOR.INVALID_VAR_NAME_TEXT);
  }
}
